import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/icons";

type Props = {
  onEdit: () => void;
  onRemove: () => void;
  /** Accessible label for the trigger, e.g. the member's name. */
  label?: string;
};

const MENU_WIDTH = 160;
const MENU_HEIGHT = 84; // two items + padding; used for flip-above + vertical clamp
const GAP = 4;
const EDGE = 8; // keep the menu at least this far from any viewport edge

/**
 * Kebab row-actions menu (Edit / Remove) for the Company-accounts table
 * (Figma 15604-78939). The menu is portalled to <body> with fixed coordinates:
 * the table body is `overflow-x-auto`, which the spec promotes to
 * `overflow-y: auto`, so an in-flow `absolute` menu on the last row gets clipped
 * by the scroller. Portalling escapes it and the menu floats over everything at
 * every viewport. Focus is managed (open → first item, close → trigger) because
 * the portal otherwise breaks natural Tab order and would strand keyboard users
 * at the end of <body> beside an orphaned open menu.
 */
export function RowActionsMenu({ onEdit, onRemove, label = "Actions" }: Props) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  // Guards the focus-on-open effect so repositioning (scroll/resize) doesn't
  // keep yanking focus back to the first item while the menu is open.
  const focusedRef = useRef(false);

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  // Place the menu under the trigger, right-aligned, clamped to the viewport on
  // BOTH axes; flip above when it would overflow the bottom. Recomputed on
  // scroll (capture: true catches the table's inner horizontal scroll) + resize.
  useLayoutEffect(() => {
    if (!open) {
      focusedRef.current = false;
      return;
    }
    function place() {
      const t = triggerRef.current;
      if (!t) return;
      const r = t.getBoundingClientRect();
      const left = Math.max(
        EDGE,
        Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - EDGE),
      );
      const below = r.bottom + GAP;
      const raw =
        below + MENU_HEIGHT > window.innerHeight - EDGE ? r.top - GAP - MENU_HEIGHT : below;
      const top = Math.max(EDGE, Math.min(raw, window.innerHeight - MENU_HEIGHT - EDGE));
      setPos({ top, left });
    }
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  // Move focus into the menu once it mounts (pos is set the tick after open) so
  // keyboard users reach it despite the portal breaking natural Tab order.
  // Fires once per open (focusedRef).
  useEffect(() => {
    if (open && pos && !focusedRef.current) {
      focusedRef.current = true;
      menuRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
    }
  }, [open, pos]);

  useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent) {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      // Restore focus to the trigger only when it currently sits inside the menu,
      // so an outside click that legitimately moves focus elsewhere isn't hijacked.
      close(!!menuRef.current?.contains(document.activeElement));
    }
    function onKey(e: KeyboardEvent) {
      // Escape and Tab both dismiss and return focus to the trigger. Tab would
      // otherwise send focus to the end of <body> (where the portal lives) and
      // strand the user beside an orphaned open menu.
      if (e.key === "Escape") {
        close(true);
      } else if (e.key === "Tab") {
        e.preventDefault();
        close(true);
      }
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        className={[
          "flex size-8 items-center justify-center rounded-[4px] transition-colors",
          open ? "bg-grey-100 text-black" : "text-grey-600 hover:bg-grey-50 hover:text-black",
        ].join(" ")}
      >
        <Icon name="more_vert" size={20} />
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label={label}
            style={{ position: "fixed", top: pos.top, left: pos.left, width: MENU_WIDTH }}
            className="z-50 flex flex-col rounded-[4px] border border-grey-200 bg-white p-1 shadow-soft"
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close(false); // the EditMemberModal that opens next manages its own focus
                onEdit();
              }}
              className="flex items-center gap-2 rounded-[4px] px-3 py-2 text-left text-small font-medium text-black transition-colors hover:bg-grey-50"
            >
              <Icon name="edit" size={20} className="text-grey-600" />
              Edit
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close(false); // the ConfirmDialog that opens next manages its own focus
                onRemove();
              }}
              className="flex items-center gap-2 rounded-[4px] px-3 py-2 text-left text-small font-medium text-red-600 transition-colors hover:bg-red-100"
            >
              <Icon name="delete" size={20} />
              Remove
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}
